import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  CalendarCheck,
  Wand2,
  Shield,
  Sparkles,
  Layers,
  Droplets,
  Sun,
  Truck,
  BadgeCheck,
  Star,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Marquee from "@/components/Marquee";
import { IMAGES, LOGO_URL } from "@/data/catalog";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Reveal = ({ children, delay = 0, className }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 36 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

const Eyebrow = ({ children }) => (
  <p className="font-mono text-xs tracking-[0.25em] text-velocity uppercase font-bold mb-4">{children}</p>
);

const SERVICES = [
  {
    icon: Layers,
    title: "Full Color-Change Wraps",
    desc: "A complete new identity in cast vinyl — satin, gloss, metallic, color-shift or chrome. Tucked edges, zero paint lines.",
    img: IMAGES.redCar,
    big: true,
  },
  {
    icon: Sparkles,
    title: "Chrome Delete & Blackout",
    desc: "Trim, grilles, badges and window surrounds finished in deep gloss or satin black.",
  },
  {
    icon: Shield,
    title: "Paint Protection Film",
    desc: "Self-healing urethane armor for high-impact zones. Invisible, or full stealth matte conversions.",
    img: IMAGES.polishing,
  },
  {
    icon: Droplets,
    title: "Ceramic Coating",
    desc: "Hydrophobic top layer over paint or film — easier washes, deeper gloss, longer life.",
  },
  {
    icon: Sun,
    title: "Ceramic Window Tint",
    desc: "Heat-rejecting ceramic tint that keeps cabins cool and interiors protected.",
  },
  {
    icon: Truck,
    title: "Commercial & Fleet Graphics",
    desc: "Rolling brand assets — from single work vans to full fleets with consistent color across every unit.",
    img: IMAGES.workshop,
    big: true,
  },
];

const PROCESS = [
  { n: "01", title: "Decontamination", desc: "Foam wash, clay-bar treatment and an alcohol wipe-down so film bonds to a surgically clean surface." },
  { n: "02", title: "Disassembly", desc: "Handles, badges, mirrors and trim removed where needed so vinyl tucks behind every edge." },
  { n: "03", title: "Seamless Tucking", desc: "Panels laid one at a time with calibrated heat — edges wrapped and sealed, never cut on the car." },
  { n: "04", title: "Infrared Curing", desc: "Post-heat curing locks the adhesive memory, then a panel-by-panel inspection under shop light." },
];

const GALLERY = [
  { src: IMAGES.blackCar, title: "Stealth Black Exotic", cat: "Satin & Matte" },
  { src: IMAGES.greenCar, title: "Satin Mamba Green", cat: "Satin & Matte" },
  { src: IMAGES.redCar, title: "Gloss Rosso Nose", cat: "Gloss & Metallic" },
  { src: IMAGES.orangeCar, title: "Sunset Metallic GT", cat: "Gloss & Metallic" },
  { src: IMAGES.heroAlt, title: "Dark Studio Fascia", cat: "Exotic" },
  { src: IMAGES.detailBay, title: "Ceramic Detail Bay", cat: "Studio Craft" },
  { src: IMAGES.craftHands, title: "Hand-Laid Hood Film", cat: "Studio Craft" },
  { src: IMAGES.workshop, title: "Full Body In Progress", cat: "Studio Craft" },
];

const CATS = ["All", "Satin & Matte", "Gloss & Metallic", "Exotic", "Studio Craft"];

const REVIEWS = [
  { name: "Marcus T.", car: "2022 Corvette C8", text: "The finish is better than factory paint. Edges are invisible — people at cars & coffee refuse to believe it's vinyl." },
  { name: "Danielle R.", car: "Range Rover Sport", text: "They walked me through film options, sent progress photos daily, and delivered two days early. Flawless experience." },
  { name: "Omar K.", car: "Fleet of 6 Sprinters", text: "Color-matched our brand perfectly across all six vans. Customers literally wave our drivers down now." },
];

const FAQS = [
  { q: "How long does a full wrap take?", a: "Most full color-change wraps take three to five working days in the studio. Full PPF coverage and fleet projects get a custom schedule after the vehicle survey." },
  { q: "How long will the wrap last?", a: "Premium cast films from 3M, Avery Dennison and Hexis carry manufacturer ratings of five to seven years when installed and maintained correctly." },
  { q: "Will a wrap damage my paint?", a: "No — film protects the paint underneath from UV, chips and light scratches. On healthy factory paint, removal leaves the original finish intact." },
  { q: "Can I wash my car normally?", a: "Hand washing or touchless washes are recommended. We include a care card with every delivery, and ceramic over-coating makes maintenance even easier." },
  { q: "What does a wrap cost?", a: "It depends on vehicle size, film family and accent work. Use the Create Your Own Wrap studio for an instant ballpark, then we confirm with a written quote." },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <main data-testid="home-view">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Black supercar under studio lights"
            className="h-full w-full object-cover object-center opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian/90 via-transparent to-transparent" />
        </motion.div>

        <div className="absolute -left-20 top-1/3 h-96 w-96 rounded-full bg-velocity/15 blur-[140px]" />

        <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 pb-14 pt-32 w-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: -20 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8 inline-block"
          >
            <img
              src={LOGO_URL}
              alt="G5 Auto Restyling badge"
              className="h-16 w-16 sm:h-24 sm:w-24 rounded-full object-cover ring-2 ring-velocity animate-pulse-glow"
            />
          </motion.div>

          <h1 className="font-display font-black uppercase tracking-tight leading-[0.92] text-4xl sm:text-7xl lg:text-8xl">
            {["Your Vision.", "Wrapped In", "Precision."].map((l, i) => (
              <span key={l} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                  className={`block ${i === 1 ? "text-outline" : ""} ${i === 2 ? "text-velocity" : ""}`}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          >
            G5 Auto Restyling is a bespoke wrap atelier — color-change wraps, paint protection, ceramic and custom
            vinyl, engineered panel by panel.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Link
                to="/customizer"
                data-testid="hero-launch-studio-btn"
                className="inline-flex items-center justify-center gap-2 bg-velocity hover:bg-velocitydark text-white font-semibold px-5 py-3 text-sm sm:text-base sm:px-7 sm:py-3.5 rounded-full transition-all duration-200 shadow-[0_0_30px_rgba(255,42,59,0.4)] hover:shadow-[0_0_45px_rgba(255,42,59,0.6)] hover:-translate-y-0.5"
              >
                <Wand2 className="h-5 w-5" />
                Launch Wrap Studio
              </Link>
              <Link
                to="/tint"
                data-testid="hero-tint-lab-btn"
                className="inline-flex items-center justify-center gap-2 border border-velocity/60 text-velocity hover:bg-velocity hover:text-white font-semibold px-5 py-3 text-sm sm:text-base sm:px-7 sm:py-3.5 rounded-full transition-all duration-200"
              >
                <Sun className="h-5 w-5" />
                Tint Lab
              </Link>
            </div>
            <Link
              to="/book"
              data-testid="hero-book-btn"
              className="inline-flex items-center justify-center gap-2 border border-edge bg-gunmetal/60 backdrop-blur hover:border-velocity/60 hover:text-white text-slate-200 font-semibold px-5 py-3 text-sm sm:text-base sm:px-7 sm:py-3.5 rounded-full transition-all duration-200 w-full sm:w-auto"
            >
              <CalendarCheck className="h-5 w-5" />
              Book Now — Live Availability
            </Link>
            <Link
              to="/contact"
              data-testid="hero-quote-btn"
              className="inline-flex items-center justify-center sm:justify-start gap-2 text-slate-400 hover:text-white text-sm font-medium transition-colors duration-200 px-2"
            >
              Get a Free Quote
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px bg-edge/60 rounded-xl overflow-hidden border border-edge max-w-2xl"
          >
            {[
              ["340+", "Wraps Delivered"],
              ["12", "Film Brands Stocked"],
              ["1 YR", "Workmanship Warranty"],
              ["48H", "Quote Turnaround"],
            ].map(([v, l]) => (
              <div key={l} className="bg-obsidian/80 backdrop-blur px-3 py-3">
                <p className="font-display font-black text-xl sm:text-2xl text-white">{v}</p>
                <p className="font-mono text-[10px] tracking-[0.2em] text-steel uppercase mt-1">{l}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      {/* SERVICES BENTO */}
      <section id="services" className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-32">
        <Reveal>
          <Eyebrow>What the studio does</Eyebrow>
          <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-5xl max-w-2xl leading-none">
            Restyle. Protect. <span className="text-velocity">Transform.</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06} className={s.big ? "md:col-span-2" : ""}>
              <div
                data-testid={`service-card-${i}`}
                className="group relative h-full min-h-[210px] sm:min-h-[260px] rounded-2xl border border-edge bg-panel overflow-hidden hover:border-velocity/50 transition-colors duration-300"
              >
                {s.img && (
                  <>
                    <img
                      src={s.img}
                      alt={s.title}
                      className="absolute inset-0 h-full w-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-panel via-panel/60 to-transparent" />
                  </>
                )}
                <div className="relative p-5 sm:p-7 flex flex-col h-full justify-end">
                  <s.icon className="h-7 w-7 text-velocity mb-4" />
                  <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed max-w-md">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TINT LAB TEASER */}
      <section data-testid="home-tint-lab-section" className="max-w-7xl mx-auto px-4 sm:px-8 pb-16 sm:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch rounded-2xl border border-edge bg-panel overflow-hidden">
          <Reveal className="p-7 sm:p-10">
            <Eyebrow>Interactive — new</Eyebrow>
            <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-5xl leading-none">
              The Tint <span className="text-velocity">Lab.</span>
            </h2>
            <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed max-w-md">
              Drag the darkness dial from 70% clear ceramic down to 5% limo and watch your cabin glass respond live —
              with heat rejection and glare numbers that move with it.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["5% Limo", "20% Dark", "35% Medium", "50% Light", "70% Clear"].map((c) => (
                <span key={c} className="px-3 py-1.5 rounded-full border border-edge font-mono text-[10px] tracking-wider text-slate-400 uppercase">
                  {c}
                </span>
              ))}
            </div>
            <Link
              to="/tint"
              data-testid="home-tint-lab-btn"
              className="mt-6 inline-flex items-center gap-2 bg-velocity hover:bg-velocitydark text-white font-semibold px-6 py-3 text-sm sm:text-base rounded-full transition-all duration-200 shadow-[0_0_24px_rgba(255,42,59,0.35)] hover:-translate-y-0.5"
            >
              <Sun className="h-5 w-5" /> Open the Tint Lab
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="relative min-h-[240px]">
            <div className="relative h-full w-full" style={{ aspectRatio: "16 / 9" }}>
              <img
                src="/vehicles/trx.jpg"
                alt="Ram 1500 TRX shown with 20% ceramic window tint"
                className="absolute inset-0 h-full w-full"
                style={{ objectFit: "fill" }}
                loading="lazy"
              />
              <div
                className="absolute inset-0"
                style={{
                  WebkitMaskImage: "url(/vehicles/trx-glass.png)",
                  maskImage: "url(/vehicles/trx-glass.png)",
                  WebkitMaskSize: "100% 100%",
                  maskSize: "100% 100%",
                  maskRepeat: "no-repeat",
                  background: "#04060B",
                  opacity: 0.82,
                }}
              />
              <span className="absolute top-4 right-4 font-mono text-[10px] tracking-[0.25em] bg-obsidian/70 border border-edge rounded-full px-3 py-1.5 text-velocity uppercase">
                Shown at VLT 20%
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-y border-edge bg-gunmetal carbon-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-32">
          <Reveal>
            <Eyebrow>The G5 formula</Eyebrow>
            <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-5xl leading-none">
              Four Stages. <span className="text-outline">Zero Shortcuts.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div data-testid={`process-step-${i}`} className="relative rounded-2xl border border-edge bg-obsidian/80 p-5 sm:p-7 h-full hover:border-velocity/40 transition-colors duration-300">
                  <span className="font-display font-black text-4xl text-velocity/25">{p.n}</span>
                  <h3 className="mt-4 font-display font-bold text-xl tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <GallerySection />

      {/* REVIEWS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-32">
        <Reveal>
          <Eyebrow>Word on the street</Eyebrow>
          <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-5xl leading-none">
            Drivers Talk. <span className="text-velocity">Cars Prove It.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          {REVIEWS.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.08}>
              <figure data-testid={`review-card-${i}`} className="rounded-2xl border border-edge bg-panel p-5 sm:p-7 h-full flex flex-col">
                <div className="flex gap-1 text-velocity mb-4">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <blockquote className="text-sm text-slate-300 leading-relaxed flex-1">"{r.text}"</blockquote>
                <figcaption className="mt-5 pt-4 border-t border-edge">
                  <p className="font-display font-bold">{r.name}</p>
                  <p className="font-mono text-[11px] tracking-wider text-steel uppercase">{r.car}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 pb-16 sm:pb-32">
        <Reveal>
          <Eyebrow>Answers</Eyebrow>
          <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-5xl leading-none mb-10">
            Before You <span className="text-outline">Ask.</span>
          </h2>
        </Reveal>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border border-edge rounded-xl bg-panel px-6" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="font-display font-bold text-left tracking-tight hover:text-velocity hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-slate-400 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-edge">
        <img src={IMAGES.craftHands} alt="Installer smoothing vinyl film" className="absolute inset-0 h-full w-full object-cover opacity-25" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/80 to-obsidian/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-32">
          <Reveal>
            <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-6xl leading-none max-w-3xl">
              Ready When <span className="text-velocity">You Are.</span>
            </h2>
            <p className="mt-5 max-w-xl text-slate-300 leading-relaxed">
              Build your spec in the studio in under two minutes, or send a quote request and talk it through with the
              crew.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/customizer"
                data-testid="cta-studio-btn"
                className="inline-flex items-center gap-2 bg-velocity hover:bg-velocitydark text-white font-semibold px-5 py-3 text-sm sm:text-base sm:px-7 sm:py-3.5 rounded-full transition-all duration-200 hover:-translate-y-0.5 shadow-[0_0_30px_rgba(255,42,59,0.4)]"
              >
                <Wand2 className="h-5 w-5" /> Create Your Own Wrap
              </Link>
              <Link
                to="/decals"
                data-testid="cta-decals-btn"
                className="inline-flex items-center gap-2 border border-edge bg-gunmetal/60 backdrop-blur hover:border-velocity/60 text-slate-200 font-semibold px-5 py-3 text-sm sm:text-base sm:px-7 sm:py-3.5 rounded-full transition-all duration-200"
              >
                <BadgeCheck className="h-5 w-5" /> Design a Custom Decal
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

const GallerySection = () => {
  const ref = useRef(null);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-32" ref={ref}>
      <Reveal>
        <Eyebrow>Curated transformations</Eyebrow>
        <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-5xl leading-none">
          Fresh Off <span className="text-outline">The Floor.</span>
        </h2>
      </Reveal>
      <GalleryGrid />
    </section>
  );
};

import { useState } from "react";

const GalleryGrid = () => {
  const [cat, setCat] = useState("All");
  const items = GALLERY.filter((g) => cat === "All" || g.cat === cat);
  return (
    <>
      <div className="mt-8 flex flex-wrap gap-2">
        {CATS.map((c) => (
          <button
            key={c}
            data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => setCat(c)}
            className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-wider border transition-colors duration-200 ${
              cat === c
                ? "bg-velocity border-velocity text-white"
                : "border-edge text-slate-400 hover:text-white hover:border-steel"
            }`}
          >
            {c}
          </button>
        ))}
      </div>
      <motion.div layout className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((g, i) => (
          <motion.figure
            layout
            key={g.title}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: i * 0.04 }}
            data-testid={`gallery-item-${i}`}
            className="group relative aspect-[4/5] overflow-hidden rounded-2xl border border-edge"
          >
            <img
              src={g.src}
              alt={g.title}
              loading="lazy"
              className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-transparent to-transparent opacity-80" />
            <figcaption className="absolute bottom-0 left-0 right-0 p-5">
              <p className="font-mono text-[10px] tracking-[0.25em] text-velocity uppercase">{g.cat}</p>
              <p className="font-display font-bold text-lg tracking-tight mt-1">{g.title}</p>
            </figcaption>
          </motion.figure>
        ))}
      </motion.div>
    </>
  );
};

export default Home;
